import { useEffect, useMemo, useRef, useState } from 'react'
import { Typography } from '@mui/material'

function formatRemain(totalSec: number): string {
  if (totalSec <= 0) return '00:00'
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  }
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

export type DeadlineClockFields = {
  round_opens_at?: string | null
  round_closes_at?: string | null
  server_now_iso?: string | null
  timezone?: string
  submission_open?: boolean
}

type Props = {
  status: DeadlineClockFields | null | undefined
  /** Gọi khi API refetch (đồng bộ lại offset server) */
  serverTick?: number
}

/**
 * Đếm ngược đến round_closes_at với hiệu chỉnh lệch đồng hồ theo server_now_iso (BR-02-02).
 */
export default function SubmissionDeadlineCountdown({ status, serverTick = 0 }: Props) {
  const [nowMs, setNowMs] = useState(() => Date.now())
  const offsetRef = useRef(0)

  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [])

  useEffect(() => {
    if (status?.server_now_iso) {
      offsetRef.current = new Date(status.server_now_iso).getTime() - Date.now()
    }
  }, [status?.server_now_iso, serverTick])

  const { lineOpen, lineClose, lineExpired, labelTz } = useMemo(() => {
    const tz = status?.timezone || 'Asia/Ho_Chi_Minh'
    const effectiveNow = nowMs + offsetRef.current
    const openMs = status?.round_opens_at ? new Date(status.round_opens_at).getTime() : null
    const closeMs = status?.round_closes_at ? new Date(status.round_closes_at).getTime() : null

    let openStr: string | null = null
    let closeStr: string | null = null
    let expiredStr: string | null = null

    if (openMs != null && effectiveNow < openMs) {
      const sec = Math.max(0, Math.floor((openMs - effectiveNow) / 1000))
      openStr = `Mở cửa nộp sau: ${formatRemain(sec)}`
    }

    if (closeMs != null && status?.submission_open) {
      const sec = Math.max(0, Math.floor((closeMs - effectiveNow) / 1000))
      closeStr = `Đóng cửa sau: ${formatRemain(sec)}`
    }
    if (closeMs != null && !status?.submission_open && effectiveNow >= closeMs) {
      expiredStr = 'Đã kết thúc (Expired)'
    }

    return {
      lineOpen: openStr,
      lineClose: closeStr,
      lineExpired: expiredStr,
      labelTz: tz,
    }
  }, [status, nowMs])

  if (!status?.round_closes_at && !status?.round_opens_at) return null

  return (
    <Typography variant="body2" component="div" sx={{ mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
      {lineOpen && <div>{lineOpen}</div>}
      {lineClose && <div>{lineClose}</div>}
      {lineExpired && <div>{lineExpired}</div>}
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        Múi giờ hệ thống: {labelTz}
      </Typography>
    </Typography>
  )
}
